import { Button } from '@heroui/react';
import React, { useState, useCallback } from 'react';

import { useNotification } from '../../../../contexts/NotificationContext';
import { useSetOrderAsTest } from '../../hooks/useSetOrderAsTest';

import { InternalReportingProps } from './types';

export const InternalReporting: React.FC<InternalReportingProps> = ({
  orderId,
  isTest,
}) => {
  const [disabled, setDisabled] = useState(isTest);
  const { addNotification } = useNotification();
  const { setOrderAsTest, loading } = useSetOrderAsTest();

  const handleMarkAsTest = useCallback(async () => {
    try {
      const result = await setOrderAsTest(orderId);

      if (result.success) {
        addNotification({
          title: 'Success',
          message: 'Successfully marked as a test order',
          type: 'success',
        });
        setDisabled(true);
      } else {
        addNotification({
          title: 'Error',
          message: result.message || 'Failed to mark order as test',
          type: 'error',
        });
      }
    } catch (error) {
      addNotification({
        title: 'Error',
        message: 'Failed to mark order as test',
        type: 'error',
      });
    }
  }, [orderId, setOrderAsTest, addNotification]);

  return (
    <div className="space-y-3">
      <h3 className="text-lg font-semibold text-foreground">
        Internal reporting
      </h3>

      <div className="bg-default-100 dark:bg-default-50 p-4 rounded-lg space-y-4">
        <div className="text-sm text-foreground">
          <p className="mb-2">
            Orders will be automatically marked as a &quot;Test&quot; order if
            any of the following are true:
          </p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li>
              An order is placed and the order comment is &quot;Test&quot; or
              &quot;Demo&quot;
            </li>
            <li>
              An order is placed by a user marked as a &quot;Test&quot; user
            </li>
            <li>
              An order is placed by a buyer entity marked as a &quot;Test&quot;
              entity
            </li>
            <li>
              An order is placed to a distributor entity marked as a
              &quot;Test&quot; entity
            </li>
          </ul>
        </div>

        <Button
          data-testid="markIsTest"
          variant="bordered"
          color={disabled ? 'default' : 'primary'}
          isDisabled={disabled}
          isLoading={loading}
          onPress={handleMarkAsTest}
        >
          {disabled ? 'THIS ORDER WAS MARKED AS TEST' : 'MARK AS TEST ORDER'}
        </Button>
      </div>
    </div>
  );
};
